import { ValidationResult, BaseValidatorOptions } from "./types";
import { parseTimestamp } from "../parse-timestamp";

/**
 * Options for timestamp validation
 */
export interface TimestampValidatorOptions extends BaseValidatorOptions {}

/**
 * Validate a timestamp value
 * Supports ISO 8601 format (2024-12-15T10:00:00Z) and MM/DD/YYYY format
 * @param value - The string representation of a timestamp
 * @param options - Validation options
 * @returns Validation result with the ISO timestamp string and optional error (never null)
 *
 * @example
 * ```typescript
 * const result = validateTimestamp("2024-12-15T10:00:00Z");
 * if (!result.error) {
 *   console.log(result.value); // "2024-12-15T10:00:00.000Z"
 * }
 *
 * const result2 = validateTimestamp("12/15/2024");
 * if (!result2.error) {
 *   console.log(result2.value); // "2024-12-15T00:00:00.000Z"
 * }
 *
 * const result3 = validateTimestamp("invalid");
 * // result3 = { value: "invalid", error: { field: "value", message: "...", severity: "warning" } }
 * ```
 */
export function validateTimestamp(value: string, options: TimestampValidatorOptions = {}): ValidationResult<string> {
  const { field = "value" } = options;
  const timestamp = parseTimestamp(value);
  if (!timestamp) {
    return {
      value,
      error: {
        field,
        message: `"${field}" does not match required timestamp pattern: ${value}`,
        severity: "warning",
      },
    };
  }

  return { value: timestamp };
}
